import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const SCHOOL_PHONE = '0202071417';

const Dropdown = ({ label, links }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const hasActive = links.some(l => location.pathname.startsWith(l.to));

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1
          ${hasActive ? 'bg-brand-500 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}>
        {label} ▾
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 rounded-lg shadow-xl border border-gray-700 py-1 z-50"
          style={{ background: '#1A1A1A' }}>
          {links.map(link => (
            <Link key={link.to} to={link.to} onClick={() => setOpen(false)}
              className={`block px-4 py-2 text-sm font-medium transition-all
                ${location.pathname.startsWith(link.to) ? 'bg-brand-500 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const { user, logout, isTeacher } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = p => location.pathname.startsWith(p) && p !== '/';
  const isSuperAdmin = user?.role === 'super_admin';

  const studentMainLinks = [
    { to: '/student/dashboard',  label: 'Dashboard'  },
    { to: '/student/results',    label: 'Results'    },
    { to: '/student/my-teacher', label: 'My Teacher' },
    { to: '/student/timetable',  label: 'Timetable'  },
    { to: '/student/attendance', label: 'Attendance' },
  ];
  const studentLearnLinks = [
    { to: '/student/materials', label: 'Materials' },
    { to: '/student/notes',     label: 'Notes'     },
    { to: '/student/videos',    label: 'Videos'    },
    { to: '/student/movies',    label: 'Movies'    },
  ];
  const studentPracticeLinks = [
    { to: '/student/homework', label: 'Homework' },
    { to: '/student/speaking', label: 'Speaking' },
  ];

  const teacherMainLinks = [
    { to: '/admin/dashboard', label: 'Dashboard' },
    { to: '/admin/payments',  label: 'Payments'  },
  ];
  const teacherManageLinks = [
    { to: '/admin/homework',   label: 'Homework'   },
    { to: '/admin/speaking',   label: 'Speaking'   },
    { to: '/admin/attendance', label: 'Attendance' },
    { to: '/admin/timetable',  label: 'Timetable'  },
  ];
  const teacherContentLinks = [
    { to: '/admin/materials', label: 'Materials' },
    { to: '/admin/notes',     label: 'Notes'     },
    { to: '/admin/videos',    label: 'Videos'    },
    { to: '/admin/movies',    label: 'Movies'    },
    { to: '/admin/content',   label: 'Exams'     },
  ];

  const superMainLinks = [
    { to: '/super-admin/dashboard', label: 'Dashboard' },
  ];
  const superStudentLinks = [
    { to: '/super-admin/students',     label: 'All Students' },
    { to: '/super-admin/students/add', label: 'Add Student'  },
  ];
  const superManageLinks = [
    { to: '/super-admin/cohorts',     label: 'Cohorts'     },
    { to: '/super-admin/assignments', label: 'Assignments' },
    { to: '/super-admin/attendance',  label: 'Attendance'  },
    { to: '/super-admin/timetable',   label: 'Timetable'   },
  ];
  const superSettingsLinks = [
    { to: '/super-admin/pricing',  label: 'Pricing'  },
    { to: '/super-admin/payments', label: 'Payments' },
  ];

  const allStudentLinks = [...studentMainLinks, ...studentLearnLinks, ...studentPracticeLinks];
  const allTeacherLinks = [...teacherMainLinks, ...teacherManageLinks, ...teacherContentLinks];
  const allSuperLinks = [...superMainLinks, ...superStudentLinks, ...superManageLinks, ...superSettingsLinks];

  if (!user) return null;

  return (
    <nav style={{ background: '#1A1A1A' }} className="sticky top-0 z-40 shadow-xl">
      <div className="h-0.5 bg-brand-500" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="flex flex-col w-7 h-5 rounded-sm overflow-hidden border border-gray-600 flex-shrink-0">
              <div className="flex-1" style={{ background: '#1A1A1A' }} />
              <div className="flex-1 bg-brand-500" />
              <div className="flex-1 bg-gold-400" />
            </div>
            <div>
              <p className="font-display font-bold text-white text-sm leading-tight">
                <span className="text-gold-400">Deutsch</span>Bridge Institute
              </p>
              <p className="text-gray-500 text-xs leading-tight hidden sm:block">{SCHOOL_PHONE}</p>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-0.5">
            {isSuperAdmin ? (
              <>
                {superMainLinks.map(link => (
                  <Link key={link.to} to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive(link.to) ? 'bg-brand-500 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}>
                    {link.label}
                  </Link>
                ))}
                <Dropdown label="Students" links={superStudentLinks} />
                <Dropdown label="Manage" links={superManageLinks} />
                <Dropdown label="Settings" links={superSettingsLinks} />
              </>
            ) : isTeacher ? (
              <>
                {teacherMainLinks.map(link => (
                  <Link key={link.to} to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive(link.to) ? 'bg-brand-500 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}>
                    {link.label}
                  </Link>
                ))}
                <Dropdown label="Manage" links={teacherManageLinks} />
                <Dropdown label="Content" links={teacherContentLinks} />
              </>
            ) : (
              <>
                {studentMainLinks.map(link => (
                  <Link key={link.to} to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive(link.to) ? 'bg-brand-500 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'}`}>
                    {link.label}
                  </Link>
                ))}
                <Dropdown label="Learn" links={studentLearnLinks} />
                <Dropdown label="Practice" links={studentPracticeLinks} />
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {user.name?.charAt(0) || 'U'}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-white leading-tight">{user.name}</p>
                <p className="text-xs text-gray-400 capitalize leading-tight">{user.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-lg border border-gray-700 hover:border-brand-500 hover:bg-brand-500/10 transition-all font-medium">
              Sign out
            </button>
            <button onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white">
              <div className="w-5 h-0.5 bg-white mb-1" />
              <div className="w-5 h-0.5 bg-white mb-1" />
              <div className="w-5 h-0.5 bg-white" />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-800 px-4 py-3" style={{ background: '#1A1A1A' }}>
          {(isSuperAdmin ? allSuperLinks : isTeacher ? allTeacherLinks : allStudentLinks).map(link => (
            <Link key={link.to} to={link.to} onClick={() => setOpen(false)}
              className={`block px-3 py-2 rounded-lg text-sm font-medium mb-1
                ${isActive(link.to) ? 'bg-brand-500 text-white' : 'text-gray-300 hover:bg-white/10'}`}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};